import { useState, useEffect } from "react";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import {
  Mail, RefreshCw, Search, Loader2, CheckCircle2,
  XCircle, Clock, AlertTriangle, Send, Users, HelpCircle
} from "lucide-react";

// Hours after send_event before we show "Delivered (unconfirmed)" instead of blank
const UNCONFIRMED_FALLBACK_HOURS = 4;

const DELIVERY_CONFIG = {
  sent: { label: "Delivered", icon: CheckCircle2, color: "bg-status-green-dim text-status-green border-green-200" },
  success: { label: "Delivered", icon: CheckCircle2, color: "bg-status-green-dim text-status-green border-green-200" },
  error: { label: "Failed", icon: XCircle, color: "bg-status-red-dim text-status-red border-red-200" },
  safe_mode: { label: "Safe Mode", icon: AlertTriangle, color: "bg-status-amber-dim text-status-amber border-amber-200" },
  not_configured: { label: "Not Configured", icon: AlertTriangle, color: "bg-slate-50 text-slate-600 border-slate-200" },
  unknown: { label: "Unknown", icon: Clock, color: "bg-slate-50 text-slate-500 border-slate-200" },
  unconfirmed: { label: "Delivered (unconfirmed)", icon: HelpCircle, color: "bg-sky-50 text-sky-700 border-sky-200" },
  // BM-H459: a record with no recipient and no send event has not been
  // delivered to anybody. It must say so rather than ageing into a delivery
  // claim the firm cannot evidence.
  no_recipient: { label: "No recipient", icon: AlertTriangle, color: "bg-status-red-dim text-status-red border-red-200" },
};

const OB_STATUS_CONFIG = {
  sent: { label: "OB Sent", color: "text-blue-700 bg-blue-50 border-blue-200" },
  complete: { label: "OB Complete", color: "text-status-green bg-status-green-dim border-green-200" },
  pending_review: { label: "Pending Review", color: "text-status-amber bg-status-amber-dim border-amber-200" },
  overridden: { label: "Overridden", color: "text-purple-700 bg-purple-50 border-purple-200" },
  not_sent: { label: "Not Sent", color: "text-slate-500 bg-slate-50 border-slate-200" },
};

/**
 * Resolve the effective delivery status for a record.
 *
 * If the backend has a definitive status (sent/success/error/safe_mode/not_configured)
 * we use it directly.  If the status is unknown/blank but an email_sent_at timestamp
 * exists and is older than UNCONFIRMED_FALLBACK_HOURS, we show "unconfirmed" instead
 * of leaving the badge blank — covers no-reply inboxes where the pixel never fires.
 *
 * BM-H459, 9 September 2026. This used to read
 * ``record.email_sent_at || record.created_at``. There is no send event in
 * ``created_at``; it is simply when the row was made. So any onboarding
 * record that had never been sent to anybody aged into
 * "Delivered (unconfirmed)" four hours after it was created, and stayed
 * there. Reported on a record that showed "Delivered (unconfirmed)" and
 * "OB Complete" beside "No email".
 *
 * Only a real send event may age into an assumed delivery. A record with no
 * send event and no recipient address is reported as such.
 */
export function resolveDeliveryStatus(record) {
  const raw = record.email_delivery || "unknown";
  if (["sent", "success", "error", "safe_mode", "not_configured"].includes(raw)) {
    return raw;
  }
  if (!record.email_sent_at) {
    // Never sent. Say which kind of never.
    return (record.client_email || "").trim() ? "unknown" : "no_recipient";
  }
  // Pixel hasn't confirmed — check if enough time has passed since the send
  const ageHours = (Date.now() - new Date(record.email_sent_at).getTime()) / 36e5;
  if (ageHours >= UNCONFIRMED_FALLBACK_HOURS) {
    return "unconfirmed";
  }
  return "unknown";
}

/**
 * Whether the send/resend route stays available for a record.
 *
 * BM-H459: the control used to be hidden whenever ``status === "complete"``,
 * which left a record claiming completion with no recipient and no way to
 * put one in. A record that carries no send event has not been sent to
 * anybody, whatever its status field says, so the route stays open.
 */
export function canOfferResend(record) {
  if (!record.email_sent_at) return true;
  return record.status !== "complete";
}

export default function OBEmailLog() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [resending, setResending] = useState(null);
  // Trust-context: when /onboarding/resend-ob/{ref} returns 409 with a
  // candidate list, we must NOT silently retry against the first hit. Open
  // the disambiguation modal and let the operator pick a specific candidate.
  const [ambiguousResend, setAmbiguousResend] = useState(null);
  const [ambiguousRetryBusy, setAmbiguousRetryBusy] = useState(false);

  const fetchLog = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status_filter = statusFilter;
      const res = await axios.get(`${API}/onboarding/email-log`, { params });
      setRecords(res.data.records || []);
    } catch (e) {
      toast.error("Failed to load email log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, []);

  const isAmbiguousResponse = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status !== 409 || !detail || typeof detail !== "object") return false;
    return Array.isArray(detail.candidates) && detail.candidates.length > 0;
  };

  const handleResend = async (refId) => {
    setResending(refId);
    try {
      const res = await axios.post(`${API}/onboarding/resend-ob/${refId}`);
      if (res.data.email_error) {
        toast.error(`Email delivery issue: ${res.data.email_error}`);
      } else {
        toast.success(`OB email resent for ${refId}`);
      }
      fetchLog();
    } catch (e) {
      if (isAmbiguousResponse(e)) {
        const detail = e.response.data.detail;
        setAmbiguousResend({
          originalRef: refId,
          ref: detail.ref || refId,
          candidates: detail.candidates || [],
          message: detail.message || "",
        });
      } else {
        toast.error(errorMessage(e, "Failed to resend email"));
      }
    } finally {
      setResending(null);
    }
  };

  const retryAmbiguous = async (chosenMatterRef) => {
    if (!ambiguousResend || !chosenMatterRef) return;
    setAmbiguousRetryBusy(true);
    try {
      const res = await axios.post(`${API}/onboarding/resend-ob/${chosenMatterRef}`);
      if (res.data.email_error) {
        toast.error(`Email delivery issue: ${res.data.email_error}`);
      } else {
        toast.success(`OB email resent for ${chosenMatterRef}`);
      }
      setAmbiguousResend(null);
      fetchLog();
    } catch (e) {
      if (isAmbiguousResponse(e)) {
        const detail = e.response.data.detail;
        setAmbiguousResend({
          originalRef: chosenMatterRef,
          ref: detail.ref || chosenMatterRef,
          candidates: detail.candidates || [],
          message: detail.message || "",
        });
      } else {
        toast.error(errorMessage(e, "Retry failed"));
      }
    } finally {
      setAmbiguousRetryBusy(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") fetchLog();
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
  };

  const delivered = records.filter(r => ["sent", "success"].includes(r.email_delivery));
  const unconfirmed = records.filter(r => resolveDeliveryStatus(r) === "unconfirmed");
  const failed = records.filter(r => r.email_delivery === "error");
  const pending = records.filter(r => !["sent", "success", "error", "unconfirmed"].includes(resolveDeliveryStatus(r)));

  return (
    <div data-testid="ob-email-log">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-teal">OB Email Log</h2>
        </div>
        <p className="text-sm text-slate-500">Track all client onboarding emails sent from no-reply@bonsai.law — resend if delivery failed</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="email-log-filters">
        <div className="flex gap-2 flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by name, email, or ID..."
            className="max-w-xs"
            data-testid="email-log-search"
          />
          <Button onClick={fetchLog} variant="outline" size="sm" data-testid="email-log-search-go">
            <Search className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); }}>
            <SelectTrigger className="w-[180px]" data-testid="email-log-status-filter">
              <SelectValue placeholder="Filter by OB status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="sent">OB Sent</SelectItem>
              <SelectItem value="complete">OB Complete</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="overridden">Overridden</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchLog} variant="ghost" size="sm" data-testid="email-log-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="bg-status-green-dim border border-green-200 rounded-md px-3 py-2 text-sm">
          <span className="font-semibold text-status-green">{delivered.length}</span>
          <span className="text-status-green ml-1">Delivered</span>
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sm">
          <span className="font-semibold text-sky-700">{unconfirmed.length}</span>
          <span className="text-sky-600 ml-1">Delivered (unconfirmed)</span>
        </div>
        <div className="bg-status-red-dim border border-red-200 rounded-md px-3 py-2 text-sm">
          <span className="font-semibold text-status-red">{failed.length}</span>
          <span className="text-status-red ml-1">Failed</span>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
          <span className="font-semibold text-slate-700">{pending.length}</span>
          <span className="text-slate-500 ml-1">Unknown/Pending</span>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-sm">
          <span className="font-semibold text-blue-700">{records.length}</span>
          <span className="text-blue-500 ml-1">Total</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading email log...
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="email-log-empty">
          <Mail className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No OB emails found</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="email-log-list">
          {records.map((r) => {
            const effectiveStatus = resolveDeliveryStatus(r);
            const deliveryCfg = DELIVERY_CONFIG[effectiveStatus] || DELIVERY_CONFIG.unknown;
            const DeliveryIcon = deliveryCfg.icon;
            const obCfg = OB_STATUS_CONFIG[r.status] || OB_STATUS_CONFIG.not_sent;

            return (
              <div
                key={r.matter_reference || r.provisional_id}
                className="border border-slate-200 rounded-md p-4 hover:border-teal/20 transition-colors bg-white"
                data-testid={`email-log-${r.matter_reference || r.provisional_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-slate-800">{r.client_name || "Unknown"}</span>
                      <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-mono">{r.matter_reference || r.provisional_id}</span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border ${deliveryCfg.color}`}
                        title={effectiveStatus === "unconfirmed" ? `Pixel not received after ${UNCONFIRMED_FALLBACK_HOURS}h — send event recorded, delivery assumed` : undefined}
                      >
                        <DeliveryIcon className="w-3 h-3" /> {deliveryCfg.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-md border ${obCfg.color}`}>
                        {obCfg.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mb-1">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {r.client_email || "No email"}
                      </span>
                      {r.fee_earner && <span>Fee earner: {r.fee_earner}</span>}
                      {r.email_sent_at && <span>Sent: {formatDate(r.email_sent_at)}</span>}
                      {!r.email_sent_at && r.created_at && <span>Created: {formatDate(r.created_at)}</span>}
                    </div>
                    {r.joint_clients && r.joint_clients.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <Users className="w-3 h-3" />
                        <span>Joint: {r.joint_clients.map(jc => `${jc.name} (${jc.email})`).join(", ")}</span>
                      </div>
                    )}
                    {r.email_error && (
                      <div className="mt-1 text-xs text-status-red bg-status-red-dim px-2 py-1 rounded">
                        Error: {r.email_error}
                      </div>
                    )}
                    {effectiveStatus === "no_recipient" && (
                      <div className="mt-1 text-xs text-status-red bg-status-red-dim px-2 py-1 rounded">
                        No onboarding email has been sent for this record and no recipient address is held, so its status is not evidence that anything reached the client. Add the recipient and send.
                      </div>
                    )}
                    {effectiveStatus === "unconfirmed" && (
                      <div className="mt-1 text-xs text-sky-600 bg-sky-50 px-2 py-1 rounded">
                        Pixel confirmation not received — email was sent but the recipient may not have opened it yet, or is using a no-render inbox. Resend only if client reports non-receipt.
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canOfferResend(r) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                        onClick={() => handleResend(r.matter_reference || r.provisional_id)}
                        disabled={resending === (r.matter_reference || r.provisional_id)}
                        data-testid={`resend-${r.matter_reference || r.provisional_id}`}
                      >
                        {resending === (r.matter_reference || r.provisional_id) ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3 mr-1" />
                        )}
                        Resend
                      </Button>
                    )}
                    {!canOfferResend(r) && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-status-green-dim text-status-green border border-green-200">
                        <CheckCircle2 className="w-3 h-3" /> Completed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ambiguous resend modal — backend HTTP 409 candidates list. The
          operator must pick a specific matter_reference; silent first-pick
          is forbidden. */}
      {ambiguousResend && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => !ambiguousRetryBusy && setAmbiguousResend(null)}>
          <div className="bg-white border border-status-red rounded-lg p-5 max-w-2xl w-full space-y-4"
               onClick={e => e.stopPropagation()} data-testid="ambiguous-resend-modal">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-status-red" />
              <h3 className="text-lg font-bold">Ambiguous Resend — Pick a Client</h3>
            </div>
            <p className="text-sm text-slate-600">
              The reference <span className="font-mono text-status-red">{ambiguousResend.ref}</span>{" "}
              matches <span className="font-semibold">{ambiguousResend.candidates.length}</span> onboarding records.
              The resend was <span className="text-status-red font-semibold">not sent</span>{" "}
              — pick the correct client below to retry, or cancel.
            </p>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-200 max-h-96 overflow-y-auto"
                 data-testid="ambiguous-candidate-list">
              {ambiguousResend.candidates.map((cand, idx) => {
                const targetRef = cand.matter_reference || cand.provisional_id;
                return (
                  <div key={`${targetRef}-${idx}`}
                       className="p-3 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50"
                       data-testid={`ambiguous-candidate-${targetRef}`}>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <p className="text-sm font-mono text-cyan-700 truncate">{targetRef}</p>
                      <p className="text-sm text-slate-900 truncate">{cand.client_name || "(no name)"}</p>
                      <p className="text-xs text-slate-600 truncate">{cand.client_email || "(no email)"}</p>
                      <p className="text-[11px] text-slate-500">
                        Status: <span className="text-slate-700">{cand.status || "unknown"}</span>{" · "}
                        Last activity: <span className="text-slate-700">{cand.last_activity || "—"}</span>
                      </p>
                    </div>
                    <Button size="sm"
                      onClick={() => retryAmbiguous(targetRef)}
                      disabled={ambiguousRetryBusy || !targetRef}
                      className="bg-teal text-white text-xs whitespace-nowrap"
                      data-testid={`ambiguous-pick-${targetRef}`}>
                      {ambiguousRetryBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
                      Resend OB
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm"
                onClick={() => setAmbiguousResend(null)}
                disabled={ambiguousRetryBusy}
                className="text-slate-600 text-xs"
                data-testid="ambiguous-cancel-btn">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
